/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import StaticMermaidDiagram from '@/components/charts/StaticMermaidDiagram';

/* Import configuration */
import {
    createMultilineStringConstant,
    STATIC_MERMAID_DIAGRAM_EXAMPLES,
} from '@/components/tabs/layouts/layoutExamples';

interface MermaidDiagramLayoutsTabProps {
    theme: 'light' | 'dark';
}

function MermaidDiagramLayoutsTab({ theme }: MermaidDiagramLayoutsTabProps) {
    const [selectedStaticMermaidDiagramExampleIndex, setSelectedStaticMermaidDiagramExampleIndex] =
        useState(0);
    const [staticMermaidCodeTab, setStaticMermaidCodeTab] =
        useState<'jsx' | 'data'>('jsx');

    const selectedStaticMermaidDiagramExample =
        STATIC_MERMAID_DIAGRAM_EXAMPLES[selectedStaticMermaidDiagramExampleIndex];
    const selectedStaticMermaidDiagramCode = createMultilineStringConstant(
        selectedStaticMermaidDiagramExample.codeName,
        selectedStaticMermaidDiagramExample.definition,
    );

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader
                sectionKey="mermaid-diagram"
                description="MermaidDiagram renders text-based diagram definitions as SVG diagrams. This is useful for lightweight process, sequence, and structure visualizations inside the same dashboard-driven component style."
            />

            <section className="layouts-section">
                <h3>StaticMermaidDiagram</h3>
                <p>
                    Renders a static Mermaid definition without any API request.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="static-mermaid-diagram-example"
                    >
                        Mermaid example
                    </label>
                    <select
                        id="static-mermaid-diagram-example"
                        className="select select-bordered w-auto"
                        value={selectedStaticMermaidDiagramExampleIndex}
                        onChange={(event) => {
                            setSelectedStaticMermaidDiagramExampleIndex(Number(event.target.value));
                        }}
                    >
                        {STATIC_MERMAID_DIAGRAM_EXAMPLES.map((example, index) => (
                            <option key={example.codeName} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button
                            type="button"
                            className={`tab ${staticMermaidCodeTab === 'jsx' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticMermaidCodeTab('jsx');
                            }}
                        >JSX</button>
                        <button
                            type="button"
                            className={`tab ${staticMermaidCodeTab === 'data' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticMermaidCodeTab('data');
                            }}
                        >Data</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={staticMermaidCodeTab === 'data'
                            ? selectedStaticMermaidDiagramCode
                            : `<StaticMermaidDiagram
    definition={${selectedStaticMermaidDiagramExample.codeName}}
    theme="${theme}"
/>`}
                        language={staticMermaidCodeTab === 'data' ? 'typescript' : 'jsx'}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel dashboard-panel--chart layouts-demo-panel layouts-demo-panel--plot">
                    <StaticMermaidDiagram
                        definition={selectedStaticMermaidDiagramExample.definition}
                        fillParent
                        theme={theme}
                    />
                </section>
            </section>
        </div>
    );
}

export default MermaidDiagramLayoutsTab;
